<template>
	<div class="sidebar">
		<div class="sidebar-header">
			<div class="logo-container">
				<span class="logo">Stax Metrics</span>
			</div>
		</div>

		<div class="sidebar-content">
			<ul class="nav-links">
				<li v-for="link in navLinks" :key="link.id">
					<router-link 
						:to="{ name: link.name, params: link.params }" 
						class="nav-link"
						active-class="active">
						<div class="link-content">
							<span class="link-text">{{ link.text }}</span>
						</div>
						<div class="active-indicator"></div>
					</router-link>
				</li>
			</ul>
		</div>

		<div class="user-section">
			<div class="user-profile" @click="toggleUserMenu">
				<div class="avatar">
					<template v-if="hasValidPhotoURL">
						<div v-if="isImageLoading" class="avatar-loading">
							<v-progress-circular
								indeterminate
								size="20"
								width="2"
								color="white"
							></v-progress-circular>
						</div>
						<img 
							:src="processedPhotoURL" 
							:alt="userInitials"
							class="avatar-image"
							@error="handleImageError"
							@load="handleImageLoad"
						>
					</template>
					<div v-else class="avatar-placeholder">
						{{ userInitials }}
					</div>
				</div>
				<div class="user-info">
					<div class="user-name">{{ userProfile.displayName || 'User' }}</div>
					<div class="user-email">{{ userProfile.email }}</div>
				</div>
			</div>
			
			<div class="user-menu" :class="{ 'show-menu': showUserMenu }">
				<div class="menu-item" @click="logOut">
					<v-icon size="small" class="menu-icon">mdi-logout</v-icon>
					Logout
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { projectService } from '@/services/projectService';
import { authService } from '@/services/authService';

export default {
	name: 'Sidebar',
	data() {
		return {
			navLinks: [],
			showUserMenu: false,
			userProfile: {
				displayName: '',
				email: '',
				photoURL: ''
			},
			imageLoadError: false,
			isImageLoading: true
		};
	},
	computed: {
		userInitials() {
			if (this.userProfile.displayName) {
				return this.userProfile.displayName
					.split(' ')
					.map(word => word[0])
					.join('')
					.toUpperCase()
					.slice(0, 2);
			}
			return 'U';
		},
		hasValidPhotoURL() {
			return this.userProfile.photoURL && 
				this.userProfile.photoURL.startsWith('http') && 
				!this.imageLoadError;
		},
		processedPhotoURL() {
			if (!this.userProfile.photoURL) return '';
			
			// If it's a Google profile image, use a proxy
			if (this.userProfile.photoURL.includes('googleusercontent.com')) {
				return `https://images.weserv.nl/?url=${encodeURIComponent(this.userProfile.photoURL)}&w=80&h=80&fit=cover`;
			}
			
			return this.userProfile.photoURL;
		}
	},
	methods: {
		toggleUserMenu() {
			this.showUserMenu = !this.showUserMenu;
		},
		async fetchProjects() {
			try {
				const projects = await projectService.getProjects();
				this.navLinks = projects.map(project => ({
					id: project.id,
					text: project.title,
					name: 'projectDetails',
					params: { id: project.id }
				}));
			} catch (error) {
				console.error('Error fetching projects:', error);
			}
		},
		logOut() {
			authService.logout();
			this.$router.push('/login');
			this.showUserMenu = false;
		},
		handleImageError() {
			this.imageLoadError = true;
			this.isImageLoading = false;
			this.userProfile.photoURL = '';
		},
		handleImageLoad() {
			this.isImageLoading = false;
			this.imageLoadError = false;
		},
		async loadUserProfile() {
			const userData = localStorage.getItem('user');
			if (userData) {
				try {
					const parsedData = JSON.parse(userData);
					
					// Reset image states
					this.imageLoadError = false;
					this.isImageLoading = true;
					
					// Check if photoURL is a valid URL
					let photoURL = parsedData.photoURL || '';
					if (photoURL && !photoURL.startsWith('http')) {
						photoURL = '';
					}

					// Set user profile data first
					this.userProfile = {
						displayName: parsedData.displayName || 'User',
						email: parsedData.email || '',
						photoURL: photoURL
					};

					// Only attempt to load image if we have a valid URL
					if (photoURL) {
						const img = new Image();
						img.onload = () => {
							this.handleImageLoad();
						};
						img.onerror = () => {
							this.handleImageError();
						};
						img.src = this.processedPhotoURL;
					} else {
						this.isImageLoading = false;
					}
				} catch (error) {
					console.error('Error parsing user data:', error);
					this.isImageLoading = false;
					this.userProfile = {
						displayName: 'User',
						email: '',
						photoURL: ''
					};
				}
			} else {
				this.isImageLoading = false;
				this.userProfile = {
					displayName: 'User',
					email: '',
					photoURL: ''
				};
			}
		}
	},
	created() {
		this.fetchProjects();
		this.loadUserProfile();
	},
	mounted() {
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.user-section')) {
				this.showUserMenu = false;
			}
		});
	},
	beforeUnmount() {
		document.removeEventListener('click', (e) => {
			if (!e.target.closest('.user-section')) {
				this.showUserMenu = false;
			}
		});
	}
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&display=swap');

.sidebar {
	position: relative;
	height: 100vh;
	width: 250px;
	background: linear-gradient(180deg, #2c3e50 0%, #3498db 200%);
	color: white;
	font-family: 'Poppins', sans-serif;
	box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
	overflow: hidden;
}

.sidebar-content {
	position: absolute;
	top: 70px;
	bottom: 50px;
	left: 0;
	right: 0;
	overflow-y: auto;
	padding: 0 0 10px 0;
	background: linear-gradient(225deg, #2c3e50 0%, #3498db 100%);

}



.sidebar-header {
	padding: 20px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-container {
	display: flex;
	align-items: center;
	justify-content: center;
}

.logo {
	font-size: 1.3rem;
	font-weight: 600;
	color: #ecf0f1;
}



.nav-links {
	list-style: none;
	padding: 0;
	margin: 0;
}

.nav-link {
	display: flex;
	align-items: center;
	padding: 12px 20px;
	text-decoration: none;
	color: rgba(255, 255, 255, 0.8);
	transition: all 0.3s ease;
	position: relative;
	margin: 4px 0;
}

.link-content {
	display: flex;
	align-items: center;
	width: 100%;
}

.link-text {
	font-size: 0.95rem;
	font-weight: 500;
}

.nav-link:hover {
	color: white;
	background: rgba(255, 255, 255, 0.1);
}

.nav-link.active {
	color: white;
	background: rgba(255, 255, 255, 0.15);
}

.active-indicator {
	position: absolute;
	right: 0;
	top: 0;
	height: 100%;
	width: 3px;
	background: transparent;
	transition: background-color 0.3s ease;
}

.nav-link.active .active-indicator {
	background: #3498db;
}

.user-section {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	background: linear-gradient(135deg, #3498db 0%, #2c3e50 100%);
	padding: 10px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-profile {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	padding: 6px;
	border-radius: 8px;
	transition: background-color 0.2s ease;
}

.user-profile:hover {
	background: rgba(255, 255, 255, 0.1);
}

.avatar {
	position: relative;
	width: 36px;
	height: 36px;
	border-radius: 50%;
	overflow: hidden;
	background: rgba(255, 255, 255, 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.avatar-loading {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.2);
}

.avatar-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	background-color: rgba(255, 255, 255, 0.1);
}

.avatar-placeholder {
	color: white;
	font-size: 14px;
	font-weight: 600;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(225deg, #3498db 0%, #2c3e50 100%);

}

.user-info {
	flex: 1;
	min-width: 0;
	padding-right: 4px;
}

.user-name {
	font-size: 0.85rem;
	font-weight: 500;
	color: white;
	margin-bottom: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.user-email {
	font-size: 0.7rem;
	color: rgba(255, 255, 255, 0.7);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.user-menu {
	position: absolute;
	bottom: 100%;
	left: 15px;
	right: 15px;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
	opacity: 0;
	transform: translateY(10px);
	pointer-events: none;
	transition: all 0.2s ease;
	margin-bottom: 8px;
}

.show-menu {
	opacity: 1;
	transform: translateY(0);
	pointer-events: auto;
}

.menu-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	color: #2c3e50;
	font-size: 0.9rem;
	cursor: pointer;
	transition: background-color 0.2s ease;
	border-radius: 8px;
}

.menu-item:first-child {
	border-radius: 8px 8px 0 0;
}

.menu-item:last-child {
	border-radius: 0 0 8px 8px;
	border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.menu-item:hover {
	background-color: #f8f9fa;
}

.menu-icon {
	opacity: 0.7;
}

/* Scrollbar styling */
.sidebar-content::-webkit-scrollbar {
	width: 5px;
}

.sidebar-content::-webkit-scrollbar-track {
	background: rgba(255, 255, 255, 0.1);
}

.sidebar-content::-webkit-scrollbar-thumb {
	background: rgba(255, 255, 255, 0.2);
	border-radius: 10px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
	background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 768px) {
	.sidebar {
		width: 250px;
	}
	
	.user-menu {
		position: fixed;
		bottom: 80px;
		left: 20px;
		right: 20px;
	}
}
</style>